import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useOutletContext } from "react-router-dom";
import { CRICKET_API_URL } from "../../config";

function PlayerRoles() {
    const { searchQuery } = useOutletContext() || { searchQuery: "" };

    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        fetchUnknownPlayers();
    }, []);

    // 🔥 UPDATED: API se image fetch karke tempImage mein daalna
    const fetchUnknownPlayers = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${CRICKET_API_URL}/api/cricket/admin/players/unknown`);
            const dataWithTemp = res.data.map(p => ({
                ...p,
                tempRole: p.role && p.role !== '' ? p.role : 'UNKNOWN',
                tempImage: p.image || ''
            }));
            setPlayers(dataWithTemp);
        } catch (error) {
            setMessage({ type: 'danger', text: 'Failed to load players.' });
        } finally {
            setLoading(false);
        }
    };

    const handleRoleChange = (id, newRole) => {
        setPlayers(players.map(p => p._id === id ? { ...p, tempRole: newRole } : p));
    };

    // 🔥 NAYA: Image URL change handle karne ke liye
    const handleImageChange = (id, newImageUrl) => {
        setPlayers(players.map(p => p._id === id ? { ...p, tempImage: newImageUrl } : p));
    };

    // 🔥 UPDATED: Role aur Image dono save karega
    const handleSaveAll = async () => {
        const updates = players
            .filter(p => p.tempRole !== 'UNKNOWN' || p.tempImage !== '')
            .map(p => ({ _id: p._id, role: p.tempRole, image: p.tempImage }));

        if (updates.length === 0) {
            setMessage({ type: 'warning', text: '⚠️ Please update at least one role or image to save.' });
            setTimeout(() => setMessage(null), 3000);
            return;
        }

        setSaving(true);
        try {
            await axios.put(`${CRICKET_API_URL}/api/cricket/admin/players/bulk-update`, { updates });
            setMessage({ type: 'success', text: `✅ Successfully updated ${updates.length} players!` });
            fetchUnknownPlayers();
        } catch (error) {
            setMessage({ type: 'danger', text: '❌ Failed to update players.' });
        } finally {
            setSaving(false);
            setTimeout(() => setMessage(null), 3000);
        }
    };

    // CSV File Read & Upload Logic (Safe & Unchanged)
    const handleCSVUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (event) => {
            const text = event.target.result;
            const rows = text.split('\n').map(row => row.trim()).filter(row => row);
            rows.shift();

            const importedPlayers = rows.map(row => {
                // 🔥 FIX: 4th column as image fetch karo
                const [name, role, credits, image] = row.split(',');
                return {
                    name: name?.trim(),
                    role: role?.trim().toUpperCase(),
                    credits: parseFloat(credits) || 9.0,
                    image: image?.trim() || "" // Image ko array me add kiya
                };
            }).filter(p => p.name && p.role);

            if (importedPlayers.length === 0) {
                setMessage({ type: 'warning', text: 'No valid data found in CSV.' });
                return;
            }

            setSaving(true);
            try {
                const res = await axios.post(`${CRICKET_API_URL}/api/cricket/admin/players/import`, { players: importedPlayers });
                setMessage({ type: 'success', text: res.data.message });
                fetchUnknownPlayers();
            } catch (error) {
                setMessage({ type: 'danger', text: '❌ CSV Upload failed.' });
            } finally {
                setSaving(false);
                if (fileInputRef.current) fileInputRef.current.value = "";
            }
        };
        reader.readAsText(file);
    };

    const displayedPlayers = players.filter(p =>
        p.name.toLowerCase().includes((searchQuery || "").toLowerCase())
    );

    return (
        <div className="w-100" style={{ fontFamily: 'sans-serif' }}>
            <style>{`
                .table-scroll-container::-webkit-scrollbar { width: 6px; }
                .table-scroll-container::-webkit-scrollbar-track { background: #f8f9fa; }
                .table-scroll-container::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 10px; }
                .table-scroll-container::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }
                
                .sticky-table-header th { 
                    position: sticky; 
                    top: 0; 
                    background-color: #f8f9fa; 
                    z-index: 2; 
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05); 
                }
            `}</style>

            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h4 className="fw-bold mb-1 text-dark">Fix Player Roles & Images</h4>
                    <p className="text-muted small mb-0">Assign roles and add image URLs to players</p>
                </div>

                <div className="d-flex gap-2">
                    <input
                        type="file"
                        accept=".csv"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleCSVUpload}
                    />
                    <button
                        className="btn fw-bold text-dark shadow-sm px-3 bg-white border d-flex align-items-center gap-2"
                        style={{ borderRadius: '6px' }}
                        onClick={() => fileInputRef.current.click()}
                        disabled={saving}
                    >
                        <i className="bi bi-file-earmark-spreadsheet text-success fs-5"></i> Import CSV
                    </button>

                    <button
                        className="btn fw-bold text-white shadow-sm px-4 d-flex align-items-center gap-2"
                        style={{ backgroundColor: '#10b981', borderRadius: '6px' }}
                        onClick={handleSaveAll}
                        disabled={saving || players.length === 0}
                    >
                        {saving ? (
                            <><span className="spinner-border spinner-border-sm"></span></>
                        ) : (
                            <><i className="bi bi-cloud-arrow-up-fill fs-5"></i> Save Changes</>
                        )}
                    </button>
                </div>
            </div>

            {message && (
                <div className={`alert alert-${message.type} shadow-sm py-2 px-3 small fw-bold rounded-3 mb-3`}>
                    {message.text}
                </div>
            )}

            <div className="card border-0 shadow-sm rounded-4">
                <div className="card-header bg-white border-bottom py-3 px-4 d-flex justify-content-between align-items-center rounded-top-4">
                    <h6 className="mb-0 fw-bold">Player List ({displayedPlayers.length})</h6>
                    <button className="btn btn-light btn-sm fw-bold border" onClick={fetchUnknownPlayers}>
                        <i className="bi bi-arrow-clockwise me-1"></i> Refresh
                    </button>
                </div>

                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5 text-muted">
                            <div className="spinner-border text-danger mb-2" role="status"></div>
                            <p className="small fw-bold">Loading Database...</p>
                        </div>
                    ) : players.length === 0 ? (
                        <div className="text-center py-5 text-success">
                            <i className="bi bi-check-circle-fill fs-1 mb-2"></i>
                            <h5 className="fw-bold">All caught up!</h5>
                        </div>
                    ) : (
                        <div className="table-responsive table-scroll-container" style={{ maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' }}>
                            <table className="table table-hover align-middle mb-0">
                                <thead className="sticky-table-header text-muted small text-uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>
                                    <tr>
                                        <th className="py-3 px-4 fw-bold border-bottom-0">Player Profile</th>
                                        <th className="py-3 fw-bold border-bottom-0 text-center">Current Status</th>
                                        <th className="py-3 px-4 fw-bold border-bottom-0 text-center">Assign Role</th>
                                        <th className="py-3 px-4 fw-bold border-bottom-0 text-end" style={{ minWidth: '220px' }}>Image URL</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayedPlayers.length > 0 ? displayedPlayers.map(player => (
                                        <tr key={player._id}>
                                            <td className="px-4 py-3">
                                                <div className="d-flex align-items-center">
                                                    {/* 🔥 IMAGE PREVIEW LOGIC */}
                                                    {player.tempImage ? (
                                                        <img src={player.tempImage} alt="Profile" className="rounded-circle me-3 border" style={{ width: '35px', height: '35px', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }} />
                                                    ) : null}
                                                    <div className="bg-light rounded-circle d-flex align-items-center justify-content-center me-3 border text-primary fw-bold" style={{ width: '35px', height: '35px', display: player.tempImage ? 'none' : 'flex' }}>
                                                        {player.name.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-0 fw-bold text-dark" style={{ fontSize: '14px' }}>{player.name}</h6>
                                                        <small className="text-muted" style={{ fontSize: '10px' }}>ID: {player._id.slice(-6).toUpperCase()}</small>
                                                    </div>
                                                </div>
                                            </td>

                                            <td className="py-3 text-center">
                                                <span className="badge bg-danger bg-opacity-10 text-danger px-3 py-1 rounded-pill border border-danger border-opacity-25" style={{ fontSize: '11px' }}>
                                                    {player.role || 'UNKNOWN'}
                                                </span>
                                            </td>

                                            <td className="px-4 py-3 text-center">
                                                <select
                                                    className={`form-select form-select-sm shadow-none d-inline-block fw-bold border-secondary-subtle ${player.tempRole !== 'UNKNOWN' ? 'bg-success bg-opacity-10 text-success border-success' : 'bg-light text-secondary'}`}
                                                    style={{ width: '130px', borderRadius: '6px' }}
                                                    value={player.tempRole}
                                                    onChange={(e) => handleRoleChange(player._id, e.target.value)}
                                                >
                                                    <option value="UNKNOWN" disabled>Select Role...</option>
                                                    <option value="WK">Wicket Keeper</option>
                                                    <option value="BAT">Batsman</option>
                                                    <option value="AR">All Rounder</option>
                                                    <option value="BOWL">Bowler</option>
                                                </select>
                                            </td>

                                            <td className="px-4 py-3 text-end">
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm shadow-none border-secondary-subtle"
                                                    placeholder="Paste Image URL here..."
                                                    style={{ width: '100%', borderRadius: '6px', fontSize: '12px' }}
                                                    value={player.tempImage}
                                                    onChange={(e) => handleImageChange(player._id, e.target.value)}
                                                />
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="4" className="text-center py-4 text-muted small fw-bold">
                                                No players found matching your search.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default PlayerRoles;